export function isExecutionExpiredError(error: unknown): boolean {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return raw.includes("execution_expired") || /API Error 410\b/i.test(raw);
}

export function isExecutionPayloadExpired(
  payloadState?: string | null,
): boolean {
  return payloadState === "purged" || payloadState === "expired";
}

export function isRawTechnicalError(text: string | null | undefined): boolean {
  const value = text?.trim() ?? "";
  if (!value) {
    return false;
  }
  return (
    /^API Error \d+:/i.test(value) ||
    value.includes("execution_expired") ||
    value.includes("archive_unavailable") ||
    /^Error:\s+/i.test(value)
  );
}

export function sanitizeExecutionDetailError(
  error: unknown,
  fallback: string,
): string | null {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (isRawTechnicalError(trimmed)) {
    return fallback;
  }
  return trimmed;
}

function hasResultPayload(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
    && Object.keys(value as Record<string, unknown>).length > 0;
}

export type ExecutionResultBody =
  | { kind: "result"; data: Record<string, unknown> }
  | { kind: "expired" }
  | { kind: "message"; content: string }
  | { kind: "empty" };

export function resolveExecutionResultBody(input: {
  resultData?: Record<string, unknown> | null;
  executionResult?: Record<string, unknown> | null;
  messageContent?: string | null;
  expired?: boolean;
}): ExecutionResultBody {
  if (hasResultPayload(input.resultData)) {
    return { kind: "result", data: input.resultData };
  }
  if (hasResultPayload(input.executionResult)) {
    return { kind: "result", data: input.executionResult };
  }
  if (input.expired) {
    return { kind: "expired" };
  }
  const content = input.messageContent?.trim() ?? "";
  if (content) {
    return { kind: "message", content };
  }
  return { kind: "empty" };
}
